import { MoreVertical } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { useTranslate } from 'ra-core';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import { useMuteUser } from '../../../api/hooks/sendbird/useMuteUser';
import { useFetchChannelMembers } from '../../../api/hooks/sendbird/useFetchChannelMembers';
import { useFetchChannelOperators } from '../../../api/hooks/sendbird/useFetchChannelOperators';
import { useKickMember } from '../../../api/hooks/sendbird/useKickMember';
import { useToggleOperatorsStatus } from '../../../api/hooks/sendbird/useToggleOperatorsStatus';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { type User } from '../UserInfiniteList';

interface AllMembersModalDotsMenuProps {
  user: User;
}

const AllMembersModalDotsMenu: FC<AllMembersModalDotsMenuProps> = ({
  user,
}) => {
  const translate = useTranslate();
  const { channel } = useChannelSettingsContext();
  const { data: channelMembers } = useFetchChannelMembers();
  const { data: channelOperators } = useFetchChannelOperators();
  const { mutate: patchMuteUser, isPending: isMutePending } = useMuteUser();
  const { mutate: kickMember, isPending: isKickPending } = useKickMember();
  const {
    mutate: toggleOperatorsStatus,
    isPending: isToggleOperatorStatusPending,
  } = useToggleOperatorsStatus();

  const userChannelMembership = useMemo(() => {
    if (!channelMembers || !channelOperators) return null;

    const isMuted = channelMembers.some(
      (member) => member.userId === user.userId && member.isMuted
    );
    const isOperator = channelOperators.some(
      (operator) => operator.userId === user.userId
    );

    return { isMuted, isOperator };
  }, [channelMembers, channelOperators, user.userId]);

  if (!userChannelMembership) return null;

  const handleOperatorStatus = () => {
    toggleOperatorsStatus({
      channelUrl: channel.url,
      userIds: [user.userId],
      isOperator: !userChannelMembership.isOperator,
    });
  };

  const handleMuteStatus = () => {
    if (isMutePending) return;

    patchMuteUser({
      channelUrl: channel.url,
      userId: user.userId,
      muted: !userChannelMembership.isMuted,
    });
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate('chat.memberActions', {
              name: user.displayName,
            })}
          />
        }
      >
        <MoreVertical />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{user.displayName}</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={handleOperatorStatus}
          disabled={isToggleOperatorStatusPending}
        >
          {userChannelMembership.isOperator
            ? translate('chat.unregisterAsOperator')
            : translate('chat.registerAsOperator')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleMuteStatus} disabled={isMutePending}>
          {userChannelMembership.isMuted
            ? translate('chat.unmute')
            : translate('chat.mute')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onClick={() =>
            kickMember({ userId: user.userId, channelUrl: channel.url })
          }
          disabled={isKickPending}
        >
          {translate('chat.kick')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AllMembersModalDotsMenu;
